import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Plus, Lock, Clock, FileText, ArrowRight, AlertCircle, CheckCircle2, Award, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/currency';

const statusConfig = {
  DRAFT: { className: 'bg-muted text-muted-foreground border-border', icon: FileText },
  PENDING: { className: 'bg-warning/10 text-warning border-warning/30', icon: Clock },
  PENDING_CONFIRMATION: { className: 'bg-warning/10 text-warning border-warning/30', icon: Clock },
  BORROWER_CONFIRMED: { className: 'bg-blue-50 text-blue-700 border-blue-200', icon: CheckCircle2 },
  CLARIFICATION_REQUESTED: { className: 'bg-orange-50 text-orange-700 border-orange-200', icon: AlertCircle },
  BOTH_CONFIRMED: { className: 'bg-green-50 text-green-700 border-green-200', icon: Award },
  LOCKED: { className: 'bg-accent/10 text-accent border-accent/30', icon: Lock },
  SETTLED: { className: 'bg-green-100 text-green-800 border-green-300', icon: Shield },
};

function AgreementCard({ agreement, userId, currency: userCurrency }) {
  const { t, getTemplateLabel, getTemplateName, getStatusLabel, formatCurrency: formatCurrencyWithLang } = useLanguage();
  const status = statusConfig[agreement.status] || statusConfig.DRAFT;
  const isLender = agreement.created_by_user_id === userId;
  const lender = agreement.participants?.find(p => p.role === 'LENDER');
  const borrower = agreement.participants?.find(p => p.role === 'BORROWER');
  const currency = agreement.currency || userCurrency || 'INR';
  const outstanding = (agreement.current_total_due || agreement.principal_amount) - (agreement.total_paid || 0);
  const templateType = agreement.agreement_template || 'LOAN';

  return (
    <Link
      to={`/agreements/${agreement.id}`}
      data-testid={`agreement-card-${agreement.id}`}
      className="block bg-white border border-border shadow-sm hover:shadow-md transition-all duration-200 p-6 group"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {status.icon && <status.icon className="w-3.5 h-3.5" />}
            <span className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
              {isLender ? t('timeline.events.loan_created') : (t('timeline.events.borrower_joined') || 'Joined')}
            </span>
          </div>
          <p className="font-serif text-2xl font-bold tracking-tight">
            {formatCurrencyWithLang(agreement.principal_amount, currency)}
          </p>
          <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs uppercase tracking-wider font-semibold rounded-sm border" data-testid={`agreement-template-${agreement.id}`}>
            {getTemplateName(templateType)}
          </Badge>
        </div>
        <Badge className={`${status.className} text-xs uppercase tracking-wider font-semibold rounded-sm border`} data-testid={`agreement-status-${agreement.id}`}>
          {status.icon && <status.icon className="w-3 h-3 mr-1" />}
          {getStatusLabel(agreement.status)}
        </Badge>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{getTemplateLabel(templateType, 'principal')}</span>
          <span className="font-medium">{lender?.user_name || 'You'}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{getTemplateLabel(templateType, 'secondary')}</span>
          <span className="font-medium">{borrower?.user_name || t('loan.invite.accept_title')}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('interest.frequency')}</span>
          <span className="font-medium">
            {agreement.interest_enabled ? `${agreement.interest_rate}% ${agreement.interest_frequency || 'MONTHLY'}` : t('interest.disabled')}
          </span>
        </div>
      </div>

      {agreement.status === 'LOCKED' && (
        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('loan.detail.balance')}</span>
            <span className="font-semibold text-foreground">
              {formatCurrencyWithLang(outstanding, currency)}
            </span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end mt-3 text-sm text-muted-foreground group-hover:text-primary transition-colors">
        <span>{t('loan.detail.view_details') || 'View details'}</span>
        <ArrowRight className="w-3.5 h-3.5 ml-1 transform group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
}

export default function DashboardPage() {
  const { api, user } = useAuth();
  const { 
    t, 
    formatCurrency: formatCurrencyWithLang, 
    getStatusLabel, 
    getTemplateLabel, 
    getTemplateName 
  } = useLanguage();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('all');
  const currency = user?.currency || 'INR';

  useEffect(() => {
    api.get('/agreements')
      .then(res => setAgreements(res.data))
      .catch(() => toast.error(t('errors.server_error')))
      .finally(() => setLoading(false));
  }, [api, t]);

  const filteredAgreements = agreements.filter(a => {
    // Status filter
    if (filter === 'active' && a.status === 'LOCKED') return false;
    if (filter === 'locked' && a.status !== 'LOCKED') return false;
    
    // Template filter
    if (templateFilter !== 'all') {
      const agreementTemplate = a.agreement_template || 'LOAN';
      if (agreementTemplate !== templateFilter) return false;
    }
    
    return true;
  });

  const stats = {
    total: agreements.length,
    active: agreements.filter(a => a.status !== 'LOCKED').length,
    locked: agreements.filter(a => a.status === 'LOCKED').length,
    totalLent: agreements.filter(a => a.created_by_user_id === user?.id).reduce((sum, a) => sum + a.principal_amount, 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="dashboard-loading">
        <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight">{t('dashboard.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('dashboard.welcome_message', { name: user?.name })}</p>
        </div>
        <Link to="/agreements/create">
          <Button data-testid="create-agreement-button" className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6 rounded-sm uppercase tracking-wider font-semibold text-xs">
            <Plus className="w-4 h-4 mr-2" />
            {t('dashboard.create_agreement')}
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: t('dashboard.stats.total_loans'), value: stats.total, icon: FileText },
          { label: t('dashboard.stats.active_loans'), value: stats.active, icon: Clock },
          { label: t('loan.status.locked'), value: stats.locked, icon: Lock },
          { label: t('dashboard.stats.total_lent'), value: formatCurrencyWithLang(stats.totalLent, currency), icon: ArrowRight },
        ].map((stat, i) => (
          <div key={i} className="bg-white border border-border p-5 space-y-2" data-testid={`stat-${stat.label?.toLowerCase().replace(' ', '-')}`}>
            <div className="flex items-center gap-2 text-muted-foreground">
              <stat.icon className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wider">{stat.label}</span>
            </div>
            <p className="font-serif text-2xl font-bold">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-2 border-b border-border">
          {['all', 'active', 'locked'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-4 py-2.5 text-sm font-medium uppercase tracking-wider border-b-2 transition-colors ${
                filter === f
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(`dashboard.filters.${f}`)}
            </button>
          ))}
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t('templates.select_template')}:</span>
          <Select value={templateFilter} onValueChange={setTemplateFilter}>
            <SelectTrigger className="w-[180px] h-10 rounded-sm" data-testid="template-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('dashboard.filters.all')}</SelectItem>
              <SelectItem value="LOAN">{t('templates.loan')}</SelectItem>
              <SelectItem value="FREELANCER_WORK">{t('templates.freelancer_work')}</SelectItem>
              <SelectItem value="PROPERTY_TOKEN">{t('templates.property_token')}</SelectItem>
              <SelectItem value="VENDOR_ADVANCE">{t('templates.vendor_advance')}</SelectItem>
              <SelectItem value="PERSONAL_SETTLEMENT">{t('templates.personal_settlement')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Agreement grid */}
      {filteredAgreements.length === 0 ? (
        <div className="text-center py-20 space-y-4" data-testid="no-agreements">
          <AlertCircle className="w-10 h-10 text-muted-foreground mx-auto" />
          <p className="text-muted-foreground">{t('dashboard.no_loans')}</p>
          <Link to="/agreements/create">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-wider font-semibold text-xs h-10 px-6">
              {t('dashboard.create_agreement')}
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="agreements-grid">
          {filteredAgreements.map((agreement, i) => (
            <div key={agreement.id} className="animate-fade-in-up" style={{ animationDelay: `${i * 0.05}s` }}>
              <AgreementCard agreement={agreement} userId={user?.id} currency={currency} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
